import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Sparkles, Globe, EyeOff, Pencil, Loader2, CheckCircle2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { useAuth } from "../lib/auth";
import { TRANSLATION_KEYS } from "../i18n/keys";
import {
  fetchLanguageRollup, addLanguage, setLanguageStatus, generateTranslations,
  fetchTranslations, saveTranslation,
} from "../i18n/api";

/* Admin "Languages" tab: generate -> review -> publish.
   - Generate machine-translates only what is missing (never touches a row a
     human has saved), so it is safe to re-run after adding keys.
   - Open lets an admin correct any string; saving marks it reviewed.
   - Publish flips a flag the public endpoints read live: no deploy.
   Laid out as one card per language (not a wide table) because the app
   always renders inside a phone-width frame. */

const TOTAL_KEYS = Object.keys(TRANSLATION_KEYS).length;
const CODE_RE = /^[a-z]{2,3}(-[a-z0-9]{2,8})?$/;
const errText = (e, fallback) => e?.response?.data?.detail || fallback;

function ProgressBar({ reviewed, generated, total }) {
  const pct = (n) => (total ? Math.min(100, (n / total) * 100) : 0);
  return (
    <div
      className="h-2 w-full rounded-full bg-sand-200 overflow-hidden relative"
      role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={reviewed}
      aria-label={`${reviewed} reviewed, ${generated} generated, ${total} total`}
    >
      <div className="absolute inset-y-0 left-0 bg-gold-500/50" style={{ width: `${pct(generated)}%` }} />
      <div className="absolute inset-y-0 left-0 bg-forest-600" style={{ width: `${pct(reviewed)}%` }} />
    </div>
  );
}

/* ------------------------------------------------------------- Add locale */
function AddLanguageDialog({ open, onClose, onAdded }) {
  const [form, setForm] = useState({ code: "", name: "", nativeName: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ code: "", name: "", nativeName: "" }); }, [open]);

  const code = form.code.trim().toLowerCase();
  const valid = CODE_RE.test(code) && code !== "en" && form.name.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    try {
      await addLanguage({ code, name: form.name.trim(), nativeName: form.nativeName.trim() });
      toast.success(`${form.name.trim()} added as a draft`);
      onAdded();
      onClose();
    } catch (err) {
      toast.error(errText(err, "Couldn't add the language"));
    } finally { setSaving(false); }
  };

  const input = "w-full rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-600/30";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[min(92vw,28rem)]" data-testid="add-language-dialog">
        <DialogHeader>
          <DialogTitle>Add locale</DialogTitle>
          <DialogDescription>
            It starts as a draft: you can generate and review it before any student sees it.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <label className="block text-sm">
            <span className="font-medium">ISO code</span>
            <input className={input} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="fr, ur, id, zh-cn" maxLength={12} autoFocus data-testid="add-language-code" />
            {code === "en" ? (
              <span className="text-xs text-terracotta-600">English is the source language.</span>
            ) : code && !CODE_RE.test(code) ? (
              <span className="text-xs text-terracotta-600">Use a language code like fr or zh-cn.</span>
            ) : null}
          </label>
          <label className="block text-sm">
            <span className="font-medium">Name (in English)</span>
            <input className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Urdu" maxLength={80} data-testid="add-language-name" />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Native name <span className="text-muted-foreground font-normal">(optional)</span></span>
            <input className={input} dir="auto" value={form.nativeName} onChange={(e) => setForm({ ...form, nativeName: e.target.value })}
              placeholder="اردو" maxLength={80} data-testid="add-language-native" />
          </label>
          <DialogFooter className="gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
            <button type="submit" disabled={!valid || saving} className="btn-primary text-sm disabled:opacity-50" data-testid="add-language-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add locale
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------------------------------------------- Editor */
function EditorDialog({ language, onClose }) {
  const code = language?.code;
  const [saved, setSaved] = useState(null);       // { key: { text, reviewed } }
  const [drafts, setDrafts] = useState({});       // { key: unsaved text }
  const [savingKey, setSavingKey] = useState(null);
  const [search, setSearch] = useState("");
  const [onlyUnreviewed, setOnlyUnreviewed] = useState(false);

  useEffect(() => {
    if (!code) return undefined;
    let cancelled = false;
    setSaved(null); setDrafts({}); setSearch(""); setOnlyUnreviewed(false);
    fetchTranslations(code)
      .then((m) => { if (!cancelled) setSaved(m); })
      .catch((e) => { if (!cancelled) { toast.error(errText(e, "Couldn't load translations")); onClose(); } });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const savedText = (k) => saved?.[k]?.text ?? "";
  const isDirty = (k) => drafts[k] !== undefined && drafts[k] !== savedText(k);
  const dirtyCount = Object.keys(drafts).filter(isDirty).length;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return Object.entries(TRANSLATION_KEYS).filter(([k, en]) => {
      if (onlyUnreviewed && saved?.[k]?.reviewed) return false;
      return !q || k.toLowerCase().includes(q) || en.toLowerCase().includes(q) || savedText(k).toLowerCase().includes(q);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, onlyUnreviewed, saved]);

  const save = async (key) => {
    setSavingKey(key);
    try {
      const r = await saveTranslation(code, key, drafts[key]);
      setSaved((s) => {
        const next = { ...s };
        if (r.text) next[key] = { text: r.text, reviewed: true }; else delete next[key];
        return next;
      });
      setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
      toast.success(r.text ? "Saved and marked reviewed" : "Cleared: this string falls back to English");
    } catch (e) {
      toast.error(errText(e, "Couldn't save"));
    } finally { setSavingKey(null); }
  };

  const requestClose = () => {
    if (dirtyCount && !window.confirm(`Discard ${dirtyCount} unsaved edit${dirtyCount > 1 ? "s" : ""}?`)) return;
    onClose();
  };

  return (
    <Dialog open={!!language} onOpenChange={(o) => !o && requestClose()}>
      <DialogContent className="flex flex-col max-w-[min(94vw,40rem)] max-h-[88vh] gap-3 p-4 sm:p-6" data-testid="language-editor">
        <DialogHeader className="pr-6">
          <DialogTitle>{language?.name}{language?.nativeName ? ` · ${language.nativeName}` : ""}</DialogTitle>
          <DialogDescription>
            Edit any string, then Save. A saved string is marked Reviewed and a later Generate will never overwrite it.
            Leave a box empty to fall back to English.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search keys or text…"
            className="flex-1 min-w-[10rem] rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-600/30"
            data-testid="language-editor-search" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={onlyUnreviewed} onChange={(e) => setOnlyUnreviewed(e.target.checked)} />
            Needs review
          </label>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1 space-y-3" data-testid="language-editor-list">
          {saved === null ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Nothing matches.</div>
          ) : rows.map(([key, en]) => {
            const value = drafts[key] ?? savedText(key);
            const reviewed = saved[key]?.reviewed;
            return (
              <div key={key} className="rounded-xl border border-sand-300 bg-white p-3 space-y-2" data-testid={`editor-row-${key}`}>
                <div className="flex items-start justify-between gap-2">
                  <code className="text-[11px] text-muted-foreground break-all">{key}</code>
                  {reviewed && (
                    <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-forest-600/10 text-forest-700">
                      <CheckCircle2 className="h-3 w-3" /> Reviewed
                    </span>
                  )}
                </div>
                <div className="text-sm text-muted-foreground" dir="ltr">{en}</div>
                <div className="flex items-center gap-2">
                  <input
                    dir="auto"
                    value={value}
                    onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                    placeholder="not yet translated — falls back to English"
                    maxLength={2000}
                    className="flex-1 min-w-0 rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-600/30"
                    data-testid={`editor-input-${key}`}
                  />
                  <button onClick={() => save(key)} disabled={!isDirty(key) || savingKey === key}
                    className="btn-primary !px-4 !py-2 text-sm disabled:opacity-40 disabled:pointer-events-none"
                    data-testid={`editor-save-${key}`}>
                    {savingKey === key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------- Tab */
export default function LanguagesTab() {
  const { user } = useAuth();
  // A translator only reviews and edits the languages assigned to them: adding, generating and publishing
  // stay with admins (generating costs money, publishing changes what every user sees).
  const isTranslator = user?.role === "translator";
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);   // language row
  const [busy, setBusy] = useState({});           // { code: "generate" | "status" }

  const load = useCallback(async () => {
    try {
      setRows(await fetchLanguageRollup(TOTAL_KEYS));
    } catch (e) {
      toast.error(errText(e, "Couldn't load languages"));
      setRows((r) => r ?? []);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // While a language's tafsir / tadabbur job is running on the server, keep the progress fresh.
  const anyRunning = !!rows?.some((r) => r.content?.running);
  useEffect(() => {
    if (!anyRunning) return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [anyRunning, load]);

  const setBusyFor = (code, what) => setBusy((b) => {
    const n = { ...b };
    if (what) n[code] = what; else delete n[code];
    return n;
  });

  const generate = async (l) => {
    setBusyFor(l.code, "generate");
    try {
      const r = await generateTranslations(l.code, TRANSLATION_KEYS);
      if (r.generated > 0) toast.success(`Generated ${r.generated} new translation${r.generated > 1 ? "s" : ""} for ${l.name}`);
      else if (!r.error) toast.info(`Nothing new to generate for ${l.name}`);
      if (r.failed?.length) toast.warning(`${r.failed.length} string${r.failed.length > 1 ? "s" : ""} couldn't be translated safely and stay in English`);
      if (r.error) toast.warning(`Stopped early: ${r.error}. Run Generate again to continue.`);
      if (r.contentJob === "started") toast.info("Tafsir and tadabbur are translating in the background. You can leave this page.");
      else if (r.contentJob === "running") toast.info("Tafsir and tadabbur are already translating.");
    } catch (e) {
      toast.error(errText(e, "Generate failed"));
    } finally {
      setBusyFor(l.code, null);
      load();
    }
  };

  const toggleStatus = async (l) => {
    const publishing = l.status !== "published";
    if (publishing && l.generated === 0 && !window.confirm(`${l.name} has no translations yet, so students would still see English. Publish anyway?`)) return;
    setBusyFor(l.code, "status");
    try {
      await setLanguageStatus(l.code, publishing ? "published" : "draft");
      toast.success(publishing ? `${l.name} is now live for students` : `${l.name} unpublished`);
    } catch (e) {
      toast.error(errText(e, "Couldn't change status"));
    } finally {
      setBusyFor(l.code, null);
      load();
    }
  };

  return (
    <div className="space-y-4" data-testid="languages-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-foreground/70 max-w-xl">
          Add a language, <b>Generate</b> machine translations, correct them in <b>Open</b>, then <b>Publish</b>.
          Publishing is instant: no deploy. English is the source ({TOTAL_KEYS} strings) and can't be edited here.
        </p>
        {!isTranslator && (
          <button onClick={() => setAdding(true)} className="btn-primary text-sm" data-testid="add-language-btn">
            <Plus className="h-4 w-4" /> Add locale
          </button>
        )}
      </div>

      {rows === null ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="hifz-card text-center py-12 text-muted-foreground" data-testid="languages-empty">
          {isTranslator ? "No languages are assigned to you yet. Ask an admin to assign one." : "No languages yet. Add one to get started."}
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((l) => {
            const live = l.status === "published";
            const working = busy[l.code];
            return (
              <li key={l.code} className="hifz-card !p-4 space-y-3" data-testid={`language-row-${l.code}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-forest-700 truncate">
                      {l.name}{l.nativeName ? <span className="text-foreground/60 font-normal" dir="auto"> · {l.nativeName}</span> : null}
                    </div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">{l.code}</div>
                  </div>
                  <span
                    className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold ${
                      live ? "bg-forest-600 text-sand-100" : "bg-sand-200 text-foreground/70"
                    }`}
                    data-testid={`language-status-${l.code}`}>
                    {live ? "Published" : "Draft"}
                  </span>
                </div>

                <div className="space-y-1.5">
                  <ProgressBar reviewed={l.reviewed} generated={l.generated} total={l.total} />
                  <div className="text-xs text-muted-foreground" data-testid={`language-progress-${l.code}`}>
                    <b className="text-forest-700">{l.reviewed}</b> reviewed / <b className="text-foreground/80">{l.generated}</b> generated / {l.total} total
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid={`language-content-${l.code}`}>
                    {l.content?.running && <Loader2 className="h-3 w-3 animate-spin" />}
                    {l.content?.supported === false ? (
                      <span>Tafsir &amp; tadabbur: not available for this language yet</span>
                    ) : (
                      <span>
                        Tafsir &amp; tadabbur: <b className="text-foreground/80">{l.content?.translated ?? 0}</b> / {l.content?.total ?? 0} translated
                        {l.content?.running ? " · translating in the background…" : ""}
                        {l.content?.error && !l.content?.running ? <span className="text-terracotta-600"> · stopped: {l.content.error}. Generate again to continue.</span> : null}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {!isTranslator && <button onClick={() => generate(l)} disabled={!!working} className="btn-accent !px-4 !py-2 text-sm disabled:opacity-60"
                    data-testid={`language-generate-${l.code}`}>
                    {working === "generate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {working === "generate" ? "Generating…" : "Generate"}
                  </button>}
                  <button onClick={() => setEditing(l)} disabled={!!working} className="btn-ghost !px-4 !py-2 text-sm"
                    data-testid={`language-open-${l.code}`}>
                    <Pencil className="h-4 w-4" /> Open
                  </button>
                  {!isTranslator && <button onClick={() => toggleStatus(l)} disabled={!!working}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition ${
                      live
                        ? "border-terracotta-500/40 text-terracotta-600 hover:bg-terracotta-500/10"
                        : "border-forest-600/40 text-forest-700 hover:bg-forest-600/10"
                    }`}
                    data-testid={`language-publish-${l.code}`}>
                    {working === "status" ? <Loader2 className="h-4 w-4 animate-spin" /> : live ? <EyeOff className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
                    {live ? "Unpublish" : "Publish"}
                  </button>}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <AddLanguageDialog open={adding} onClose={() => setAdding(false)} onAdded={load} />
      <EditorDialog language={editing} onClose={() => { setEditing(null); load(); }} />
    </div>
  );
}
